import { useCallback, useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { ContactsTab, OrganizationsTab, PropertiesTab } from "@/components/directory";
import { Users, Building2, MapPin } from "lucide-react";

export type DirectoryTab = "contacts" | "organizations" | "properties";

export interface DirectoryNavigateDetail {
    tab: DirectoryTab;
    detailId?: number;
}

const TABS: { value: DirectoryTab; label: string; icon: typeof Users }[] = [
    { value: "contacts", label: "Contacts", icon: Users },
    { value: "organizations", label: "Organizations", icon: Building2 },
    { value: "properties", label: "Properties", icon: MapPin },
];

export default function Directory() {
    const [activeTab, setActiveTab] = useState<DirectoryTab>("contacts");
    const [openDetailId, setOpenDetailId] = useState<number | null>(null);

    // Listen for custom navigation events from drawers
    useEffect(() => {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent<DirectoryNavigateDetail>).detail;
            if (!detail || typeof detail !== 'object') return;
            const { tab, detailId } = detail;
            if (typeof tab !== 'string' || !TABS.some(t => t.value === tab)) return;
            setActiveTab(tab);
            const numId = detailId != null ? Number(detailId) : null;
            setOpenDetailId(numId !== null && Number.isFinite(numId) ? numId : null);
        };
        window.addEventListener("directory:navigate", handler);
        return () => window.removeEventListener("directory:navigate", handler);
    }, []);

    // Reset detailId after it's been consumed
    const consumeDetailId = useCallback(() => setOpenDetailId(null), []);

    return (
        <Layout>
            <div className="space-y-6 md:px-4 md:py-8">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <PageHeader
                        title="Directory"
                        description="Manage your contacts, organizations, and properties"
                    />
                    <div className="grid w-full grid-cols-3 gap-1 rounded-lg border bg-muted p-1 sm:flex sm:w-auto sm:flex-wrap sm:gap-0">
                        {TABS.map(({ value, label, icon: Icon }) => (
                            <Button
                                key={value}
                                variant={activeTab === value ? "secondary" : "ghost"}
                                size="sm"
                                onClick={() => { setActiveTab(value); setOpenDetailId(null); }}
                                className={`w-full justify-center sm:w-auto ${activeTab === value ? "bg-card shadow-sm hover:bg-card" : ""}`}
                            >
                                <Icon className="h-4 w-4 mr-2" />
                                {label}
                            </Button>
                        ))}
                    </div>
                </div>

                {activeTab === "contacts" && <ContactsTab openDetailId={openDetailId} onDetailConsumed={consumeDetailId} />}
                {activeTab === "organizations" && <OrganizationsTab openDetailId={openDetailId} onDetailConsumed={consumeDetailId} />}
                {activeTab === "properties" && <PropertiesTab openDetailId={openDetailId} onDetailConsumed={consumeDetailId} />}
            </div>
        </Layout>
    );
}
